import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmployeeTable = ({ employees, onViewEmployee, onEditEmployee, onDeleteEmployee, selectedEmployeeIds, onSelectionChange, userProfile, onResetFilters }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const selectedEmployees = selectedEmployeeIds || [];
  const setSelectedEmployees = onSelectionChange;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEmployees = React.useMemo(() => {
    if (!sortConfig.key) return employees;

    return [...employees].sort((a, b) => {
      // Handle nested properties and field name variations
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle field name variations
      if (sortConfig.key === 'employee_id') {
        aVal = a.employee_id || a.employeeId || '';
        bVal = b.employee_id || b.employeeId || '';
      } else if (sortConfig.key === 'full_name') {
        aVal = a.full_name || a.user_profiles?.full_name || a.name || '';
        bVal = b.full_name || b.user_profiles?.full_name || b.name || '';
      } else if (sortConfig.key === 'position') {
        aVal = a.position || a.designation || '';
        bVal = b.position || b.designation || '';
      } else if (sortConfig.key === 'hire_date') {
        aVal = a.hire_date || a.joiningDate || '';
        bVal = b.hire_date || b.joiningDate || '';
      }

      // Handle date comparison
      if (sortConfig.key === 'hire_date' && aVal && bVal) {
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) {
          return 0; // Invalid dates, don't sort
        }
        aVal = aDate.getTime();
        bVal = bDate.getTime();
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [employees, sortConfig]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmployees(employees.map(emp => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (employeeId, checked) => {
    if (checked) {
      setSelectedEmployees([...selectedEmployees, employeeId]);
    } else {
      setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      onboarding: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Onboarding' },
      onboarding_complete: { bg: 'bg-sky-100', text: 'text-sky-800', label: 'Onboarding Complete' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactive' },
      suspended: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suspended' },
      probation: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Probation' },
      on_leave: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'On Leave' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status?.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) || 'Unknown' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc'
      ? <Icon name="ArrowUp" size={16} className="text-primary" />
      : <Icon name="ArrowDown" size={16} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header Actions */}
      {selectedEmployees.length > 0 && (
        <div className="bg-primary/5 border-b border-border px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedEmployees.length} employee(s) selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" iconName="Mail">
                Send Email
              </Button>
              <Button variant="outline" size="sm" iconName="Download">
                Export
              </Button>
              <Button variant="outline" size="sm" iconName="MoreHorizontal">
                More Actions
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedEmployees.length === employees.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('employee_id')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Employee ID
                  {getSortIcon('employee_id')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Face Photo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('full_name')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Name
                  {getSortIcon('full_name')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('department')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Department
                  {getSortIcon('department')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('position')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Designation
                  {getSortIcon('position')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('hire_date')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Joining Date
                  {getSortIcon('hire_date')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedEmployees.map((employee) => (
              <tr key={employee.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedEmployees.includes(employee.id)}
                    onChange={(e) => handleSelectEmployee(employee.id, e.target.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                  {employee.employee_id || employee.employeeId || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {employee.face_photo_url ? (
                    <img
                      src={employee.face_photo_url.startsWith('http')
                        ? employee.face_photo_url
                        : `${import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`}${employee.face_photo_url}`}
                      alt="Face photo"
                      className="h-10 w-10 rounded-full object-cover border-2 border-border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`h-10 w-10 rounded-full bg-primary flex items-center justify-center`}
                    style={{ display: employee.face_photo_url ? 'none' : 'flex' }}
                  >
                    <span className="text-sm font-medium text-primary-foreground">
                      {employee?.user_profiles?.full_name?.split(' ')?.map(n => n[0])?.join('') ||
                        employee?.full_name?.split(' ')?.map(n => n[0])?.join('') ||
                        employee?.name?.split(' ')?.map(n => n[0])?.join('') ||
                        '?'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="text-sm font-medium text-foreground">
                        {employee?.user_profiles?.full_name || employee?.full_name || employee?.name || 'Unknown'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {employee?.user_profiles?.email || employee?.email || 'No email'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {employee.department || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {employee.position || employee.designation || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-GB') :
                    employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString('en-GB') :
                      'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(employee.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewEmployee(employee)}
                      iconName="Eye"
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditEmployee(employee)}
                      iconName="Edit"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteEmployee(employee)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        {sortedEmployees.map((employee) => (
          <div key={employee.id} className="border-b border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedEmployees.includes(employee.id)}
                  onChange={(e) => handleSelectEmployee(employee.id, e.target.checked)}
                  className="rounded border-border"
                />
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {employee?.user_profiles?.full_name?.split(' ')?.map(n => n[0])?.join('') ||
                      employee?.full_name?.split(' ')?.map(n => n[0])?.join('') ||
                      employee?.name?.split(' ')?.map(n => n[0])?.join('') ||
                      '?'}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground">
                    {employee?.user_profiles?.full_name || employee?.full_name || employee?.name || 'Unknown'}
                  </h3>
                  <p className="text-xs text-muted-foreground">{employee.employee_id || employee.employeeId || 'N/A'}</p>
                </div>
              </div>
              {getStatusBadge(employee.status)}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <span className="text-muted-foreground">Department:</span>
                <p className="text-foreground">{employee.department}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Designation:</span>
                <p className="text-foreground">{employee.position || employee.designation || 'N/A'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Joining Date:</span>
                <p className="text-foreground">
                  {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-GB') :
                    employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString('en-GB') :
                      'N/A'}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Email:</span>
                <p className="text-foreground text-xs">{employee.email}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewEmployee(employee)}
                iconName="Eye"
                className="flex-1"
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditEmployee(employee)}
                iconName="Edit"
                className="flex-1"
              >
                Edit
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {employees.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No employees found</h3>
          <p className="text-muted-foreground mb-6">
            We couldn't find any employees matching your current search or filters.
          </p>
          <Button 
            variant="outline" 
            iconName="RotateCcw"
            onClick={onResetFilters}
          >
            Clear all filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmployeeTable;
